<template lang="pug">
  .level.is-mobile.program-settings-form-controls
    .level-left
    .level-right
      .level-item(v-if="!disableReset")
        WtButton(
          is-text
          color="lightgrey"
          size="medium"
          @click.native="reset"
        ) RESET
      .level-item
        WtButton(
          :is-text="isText"
          bold
          color="primary"
          size="medium"
          @click.native="save"
          :disabled="disableSave"
          :loading="saveInProgress"
        ) {{saveButtonText}}
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EventBus from "@/eventBus";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "ProgramSettingsFormControls",

  components: { WtButton },

  props: {
    formKey: {
      type: String,
      default: null
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    formData: {
      type: Object,
      required: true
    },

    keysToReset: {
      type: Array,
      required: true
    },

    disableReset: {
      type: Boolean,
      default: false
    },

    isText: {
      type: Boolean,
      default: true
    },

    saveButtonText: {
      type: String,
      default: "SAVE"
    }
  },

  data() {
    return {
      saveInProgress: false
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"])
  },

  async mounted() {
    EventBus.onSaveProgramSettings($event => {
      if (this.formKey === $event.formKey) {
        this.quickSave($event.formData);
      }
    });
  },

  beforeDestroy() {
    EventBus.offSaveProgramSettings();
  },

  methods: {
    ...mapActions("program", [
      "programSettingsUpdate",
      "updateCoreMeta",
      "updateProgramSettingsCopy",
      "fetchProgramTeams"
    ]),
    ...mapActions("members", ["fetchMember"]),
    ...mapActions(["snackBar"]),

    reset() {
      /**
       * in the event we need to handle any values
       * when reset is called
       * @event reset
       */
      this.$emit("reset");
      this.$emit("disable-save", true);

      if (this.keysToReset.length > 0) {
        this.keysToReset.forEach(key => {
          this.updateProgramSettingsCopy({
            key: key,
            value: this.coreProgramMeta[key]
          });
        });
      }
    },

    async quickSave(formData) {
      this.saveInProgress = true;

      const response = await this.programSettingsUpdate({
        programId: this.$auth.user().programId,
        formData: formData
      });

      if (response.error) {
        const fields = this._.get(response, "error.fields", false);
        // If a validation error
        if (fields) {
          const firstErrorKey = Object.keys(fields)[0];
          this.$emit("form-error", fields[firstErrorKey][0]);
        } else {
          this.$emit("form-error", response.error.message);
        }
      } else {
        // if teamsOff exists and it's true
        if (formData.hasOwnProperty("teamsOff") && formData.teamsOff === 0) {
          this.snackBar({ message: "Teams enabled successfully." });
        }
      }

      this.saveInProgress = false;
    },

    async save() {
      this.saveInProgress = true;

      const response = await this.programSettingsUpdate({
        programId: this.$auth.user().programId,
        formData: this.formData
      });

      if (response.error) {
        const fields = this._.get(response, "error.fields", false);
        // If a validation error
        if (fields) {
          const firstErrorKey = Object.keys(fields)[0];
          this.$emit("form-error", fields[firstErrorKey][0]);
        } else {
          this.$emit("form-error", response.error.message);
        }
      } else {
        this.snackBar({ message: "Your settings have been updated." });
        this.$emit("disable-save", true);
      }

      this.saveInProgress = false;

      // TODO review areas where member is used and see if we can use $auth.user() instead,
      // and then remove this fetchMember call.
      // Note, After a program update, we fetchMember because for instance,
      // to be able to view the updated defaultStepGoal immediately without a full page refresh
      this.fetchMember({
        memberId: this.$auth.user().id,
        setMember: true
      });
      // also, refresh the authmember as it has the member attached
      this.$auth.fetch();
      await this.fetchProgramTeams();
    }
  }
};
</script>

<style lang="sass">
.program-settings-form-controls
  margin-top: 20px
</style>
